import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, EASE } from "../Reveal";
import { scrollToForm } from "../SiteNav";
import HEROIMG from "../../assets/HeroImage.png"

const TAGS = ["Periods", "Emotions", "Curiosities", "PCOS"];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-28%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-16 overflow-hidden">
            <div className="spotlight absolute inset-0 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 w-full grid grid-cols-12 gap-8 items-center py-24">
                <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 relative z-10">
                    <motion.p
                        data-testid="hero-overline"
                        className="overline-label text-[#F3698F] mb-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        Femipal — Women's health reimagined
                    </motion.p>

                    <MaskedLines
                        data-testid="hero-headline"
                        className="font-serif-display font-light tracking-tighter leading-[0.95] text-[clamp(3rem,8.5vw,7.5rem)]"
                        lines={[
                            <span key="l1">Healthcare that</span>,
                            <span key="l2">
                                understands <em className="italic font-medium text-[#F3698F]">her</em>
                            </span>,
                        ]}
                        delay={0.35}
                        stagger={0.12}
                    />

                    <motion.div
                        className="flex flex-wrap gap-x-4 gap-y-2 mt-10"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
                        data-testid="hero-tags"
                    >
                        {TAGS.map((t, i) => (
                            <span key={t} className="flex items-center gap-4 text-sm md:text-base text-[#D1C6DF] tracking-wide">
                                {i > 0 && <span className="w-1 h-1 rounded-full bg-[#F3698F]/70" />}
                                {t}
                            </span>
                        ))}
                    </motion.div>

                    <motion.div
                        className="mt-12 flex items-center gap-6"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1.25 }}
                    >
                        <button
                            onClick={scrollToForm}
                            data-testid="hero-join-button"
                            className="group overline-label bg-[#F3698F] text-white px-8 py-4 rounded-full hover:bg-[#E55C7E] transition-colors duration-300 flex items-center gap-3"
                        >
                            Join Founding 100
                            <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
                        </button>
                        <span className="text-sm text-[#FFFFFF] tracking-wide hidden md:block">
                            A 1-minute form. No medical details asked.
                        </span>
                    </motion.div>
                </motion.div>

                <div className="col-span-12 lg:col-span-5 relative mt-16 lg:mt-0">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.4, ease: EASE, delay: 0.6 }}
                        className="relative"
                    >
                        <div className="absolute -inset-8 spotlight pointer-events-none" />
                        <div className="relative overflow-hidden rounded-sm border hairline aspect-[3/4] max-h-[70vh] w-full">
                            <motion.img
                                src={HEROIMG}
                                alt="Portrait of a woman, calm and confident"
                                data-testid="hero-image"
                                style={{ y: imgY, scale: 1.18 }}
                                className="w-full h-full object-cover grayscale"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#17151B]/60 via-transparent to-transparent" />
                        </div>
                        <motion.div
                            className="absolute -bottom-5 -left-5 bg-[#282037] border hairline rounded-sm px-5 py-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: EASE, delay: 1.4 }}
                            data-testid="hero-founding-badge"
                        >
                            <p className="overline-label text-[#D1C6DF] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#F3698F] soft-pulse" />
                                Founding 100 — now forming
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}